import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Users, AlertCircle, CheckCircle, XCircle, ArrowLeft, Eye, ArrowLeftRight, Inbox } from 'lucide-react';
import { EvaluationAnalytics, ProviderPerformanceMetrics, AIRetestProgress } from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { ExecutionHistoryService } from '../services/executionHistoryService';
import { AppLayout } from './AppLayout';
import { ExecutionHistoryDetail } from './ExecutionHistoryDetail';
import { RetestProgressBanner } from './RetestProgressBanner';

interface EvaluationAnalyticsViewProps {
  testId?: string; // Optional prop, can also come from route params
}

export const EvaluationAnalyticsView = ({ testId: propTestId }: EvaluationAnalyticsViewProps) => {
  const { testId: routeTestId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const testId = propTestId || routeTestId;
  
  const [analytics, setAnalytics] = useState<EvaluationAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResultType, setSelectedResultType] = useState<{ providerId: string; providerName: string; status: string } | null>(null);
  const [selectedScore, setSelectedScore] = useState<{ providerId: string; providerName: string; score: number } | null>(null);
  const [selectedBreakdown, setSelectedBreakdown] = useState<{ providerId: string; providerName: string; metric: string } | null>(null);
  const [breakdownScoresList, setBreakdownScoresList] = useState<Array<{ executionId: string; prompt: string; executedAt: string; score: number; overallScore: number; reasoning: string }>>([]);
  const [executionListForResultType, setExecutionListForResultType] = useState<Array<{ executionId: string; prompt: string; executedAt: string }>>([]);
  const [executionListForScore, setExecutionListForScore] = useState<Array<{ executionId: string; prompt: string; executedAt: string; score: number; note?: string }>>([]);
  const [isLoadingExecutionList, setIsLoadingExecutionList] = useState(false);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [aiScoreStats, setAiScoreStats] = useState<Record<string, { 
    average: number; 
    count: number; 
    missing: number;
    breakdownAverages: {
      accuracy: number;
      citationQuality: number;
      completeness: number;
      legalQuality: number;
    };
  }>>({});
  const [isEnqueuingScores, setIsEnqueuingScores] = useState(false);
  const [totalMissingScores, setTotalMissingScores] = useState(0);
  const [retestProgress, setRetestProgress] = useState<AIRetestProgress | null>(null);
  const [isAIRetest, setIsAIRetest] = useState(false);
  const [sourceEvaluationTestId, setSourceEvaluationTestId] = useState<string | null>(null);
  const [showCompletedBanner, setShowCompletedBanner] = useState(false);
  const [isCancellingRetest, setIsCancellingRetest] = useState(false);
  const completedDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const evaluationApi = EvaluationApiService.getInstance();

  useEffect(() => {
    if (!testId) return;
    
    loadAnalytics();
    
    // Set up polling for real-time updates
    const interval = setInterval(loadAnalytics, 10000); // Poll every 10 seconds
    
    return () => clearInterval(interval);
  }, [testId]);

  // Check if this is an AI retest and poll progress
  useEffect(() => {
    if (!testId) return;

    let progressInterval: ReturnType<typeof setInterval> | null = null;

    const checkAndPollProgress = async () => {
      try {
        const test = await evaluationApi.getEvaluationTest(testId);
        if (!test?.sourceEvaluationTestId && test?.testOrigin !== 'Batch') return;

        setIsAIRetest(true);
        if (test.sourceEvaluationTestId) {
          setSourceEvaluationTestId(test.sourceEvaluationTestId);
        }

        const pollProgress = async () => {
          try {
            const progress = await evaluationApi.getRetestProgress(testId);
            setRetestProgress(progress);

            if (progress?.status === 'Completed') {
              setShowCompletedBanner(true);
              if (progressInterval) {
                clearInterval(progressInterval);
                progressInterval = null;
              }
              // Auto-dismiss after 5 seconds
              completedDismissTimer.current = setTimeout(() => {
                setShowCompletedBanner(false);
              }, 5000);
            } else if (progress?.status === 'Failed' || progress?.status === 'Cancelled') {
              if (progressInterval) {
                clearInterval(progressInterval);
                progressInterval = null;
              }
            }
            // Keep polling for Running and ScoringInProgress
          } catch (err) {
            console.error('Failed to load retest progress:', err);
          }
        };

        await pollProgress();
        progressInterval = setInterval(pollProgress, 10000);
      } catch (err) {
        console.error('Failed to check test origin:', err);
      }
    };

    checkAndPollProgress();

    return () => {
      if (progressInterval) clearInterval(progressInterval);
      if (completedDismissTimer.current) clearTimeout(completedDismissTimer.current);
    };
  }, [testId]);

  const loadAnalytics = async () => {
    if (!testId) return;
    
    try {
      const data = await evaluationApi.getAnalytics(testId);
      setAnalytics(data);
      setError(null);

      // Load AI scores for all executions
      await loadAIScoreStats(data);
    } catch (err: any) {
      console.error('Failed to load analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAIScoreStats = async (analyticsData: EvaluationAnalytics) => {
    if (!testId) return;

    try {
      // Get all executions
      const executions = await evaluationApi.listExecutions(testId);
      
      // Get all execution IDs
      const executionIds = executions.map(e => e.id);
      
      // Batch fetch AI scores
      const aiScores = executionIds.length > 0
        ? await evaluationApi.getAIScoresBatch({ evaluationExecutionIds: executionIds })
        : {} as Record<string, any[]>;
      
      // Calculate stats per provider
      const stats: Record<string, { 
        average: number; 
        count: number; 
        missing: number;
        breakdownAverages: {
          accuracy: number;
          citationQuality: number;
          completeness: number;
          legalQuality: number;
        };
      }> = {};
      let totalMissing = 0;

      analyticsData.providerMetrics.forEach(provider => {
        const providerScores: number[] = [];
        const accuracyScores: number[] = [];
        const citationScores: number[] = [];
        const completenessScores: number[] = [];
        const legalScores: number[] = [];
        let missingCount = 0;

        executions.forEach(exec => {
          const providerResult = exec.providerResults.find(pr => pr.providerId === provider.providerId);
          if (providerResult) {
            const executionScores = aiScores[exec.id] || [];
            const providerScore = executionScores.find(s => s.providerId === provider.providerId);
            
            if (providerScore) {
              providerScores.push(providerScore.overallScore);
              accuracyScores.push(providerScore.breakdown.accuracy);
              citationScores.push(providerScore.breakdown.citationQuality);
              completenessScores.push(providerScore.breakdown.completeness);
              legalScores.push(providerScore.breakdown.legalQuality);
            } else {
              // Check if response is valid (not empty or error)
              if (providerResult.response && !providerResult.response.startsWith('Error:')) {
                missingCount++;
                totalMissing++;
              }
            }
          }
        });

        const average = providerScores.length > 0
          ? providerScores.reduce((sum, score) => sum + score, 0) / providerScores.length
          : 0;

        const avgAccuracy = accuracyScores.length > 0
          ? accuracyScores.reduce((sum, score) => sum + score, 0) / accuracyScores.length
          : 0;

        const avgCitation = citationScores.length > 0
          ? citationScores.reduce((sum, score) => sum + score, 0) / citationScores.length
          : 0;

        const avgCompleteness = completenessScores.length > 0
          ? completenessScores.reduce((sum, score) => sum + score, 0) / completenessScores.length
          : 0;

        const avgLegal = legalScores.length > 0
          ? legalScores.reduce((sum, score) => sum + score, 0) / legalScores.length
          : 0;

        stats[provider.providerId] = {
          average,
          count: providerScores.length,
          missing: missingCount,
          breakdownAverages: {
            accuracy: avgAccuracy,
            citationQuality: avgCitation,
            completeness: avgCompleteness,
            legalQuality: avgLegal
          }
        };
      });

      setAiScoreStats(stats);
      setTotalMissingScores(totalMissing);
    } catch (err) {
      console.error('Failed to load AI score stats:', err);
    }
  };

  const handleEnqueueMissingScores = async () => {
    if (!testId) return;

    try {
      setIsEnqueuingScores(true);
      const result = await evaluationApi.enqueueAIScoringForTest(testId);
      
      // Show success message
      alert(`Enqueued ${result.enqueuedCount} AI scoring requests. Scores will be generated in the background.`);
      
      // Reload analytics after a delay
      setTimeout(() => {
        loadAnalytics();
      }, 3000);
    } catch (err: any) {
      console.error('Failed to enqueue AI scoring:', err);
      alert(`Failed to enqueue AI scoring: ${err.message}`);
    } finally {
      setIsEnqueuingScores(false);
    }
  };

  const handleRerunAllScores = async () => {
    if (!testId) return;

    const confirmed = window.confirm(
      'This will delete all existing AI scores and re-generate them. This action cannot be undone. Continue?'
    );

    if (!confirmed) return;

    try {
      setIsEnqueuingScores(true);
      const result = await evaluationApi.rerunAIScoringForTest(testId);
      
      // Show success message
      alert(`Re-running ${result.enqueuedCount} AI scoring requests. Scores will be regenerated in the background.`);
      
      // Reload analytics after a delay
      setTimeout(() => {
        loadAnalytics();
      }, 3000);
    } catch (err: any) {
      console.error('Failed to re-run AI scoring:', err);
      alert(`Failed to re-run AI scoring: ${err.message}`);
    } finally {
      setIsEnqueuingScores(false);
    }
  };

  const handleScoreClick = async (providerId: string, providerName: string, score: number) => {
    if (!testId) return;
    
    setSelectedScore({ providerId, providerName, score });
    setExecutionListForScore([]);
    setIsLoadingExecutionList(true);
    
    try {
      // Load all executions for this test
      const executions = await evaluationApi.listExecutions(testId);
      
      // Filter executions that have this provider and score
      const filteredExecutions: Array<{ executionId: string; prompt: string; executedAt: string; score: number; note?: string }> = [];
      
      for (const exec of executions) {
        const providerResult = exec.providerResults.find(pr => pr.providerId === providerId);
        if (providerResult) {
          // Fetch the execution scores using the API service
          try {
            const scoreData = await evaluationApi.getExecutionScores(providerResult.executionId);
            if (scoreData && scoreData.scores) {
              // Check if any user gave this score
              const matchingScores = scoreData.scores.filter((s: any) => s.score === score);
              if (matchingScores.length > 0) {
                // Add entry for each user who gave this score
                matchingScores.forEach((s: any) => {
                  filteredExecutions.push({
                    executionId: providerResult.executionId,
                    prompt: exec.prompt,
                    executedAt: exec.executedAt,
                    score: s.score,
                    note: s.note
                  });
                });
              }
            }
          } catch (err) {
            console.warn(`Failed to fetch scores for execution ${providerResult.executionId}:`, err);
          }
        }
      }
      
      setExecutionListForScore(filteredExecutions);
    } catch (err: any) {
      console.error('Failed to load executions:', err);
    } finally {
      setIsLoadingExecutionList(false);
    }
  };

  const handleBreakdownClick = async (providerId: string, providerName: string, metric: string) => {
    if (!testId) return;
    
    setSelectedBreakdown({ providerId, providerName, metric });
    setBreakdownScoresList([]);
    setIsLoadingExecutionList(true);
    
    try {
      // Load all executions for this test
      const executions = await evaluationApi.listExecutions(testId);
      
      // Get all execution IDs
      const executionIds = executions.map(e => e.id);
      
      // Batch fetch AI scores
      const aiScores = executionIds.length > 0
        ? await evaluationApi.getAIScoresBatch({ evaluationExecutionIds: executionIds })
        : {} as Record<string, any[]>;
      
      // Collect all scores for this metric
      const scoresList: Array<{ executionId: string; prompt: string; executedAt: string; score: number; overallScore: number; reasoning: string }> = [];
      
      executions.forEach(exec => {
        const providerResult = exec.providerResults.find(pr => pr.providerId === providerId);
        if (providerResult) {
          const executionScores = aiScores[exec.id] || [];
          const providerScore = executionScores.find(s => s.providerId === providerId);
          
          if (providerScore) {
            let value = 0;
            switch (metric) {
              case 'accuracy':
                value = providerScore.breakdown.accuracy;
                break;
              case 'citationQuality':
                value = providerScore.breakdown.citationQuality;
                break;
              case 'completeness':
                value = providerScore.breakdown.completeness;
                break;
              case 'legalQuality':
                value = providerScore.breakdown.legalQuality;
                break;
            }
            
            scoresList.push({
              executionId: providerResult.executionId,
              prompt: exec.prompt,
              executedAt: exec.executedAt,
              score: value,
              overallScore: providerScore.overallScore,
              reasoning: providerScore.reasoning
            });
          }
        }
      });
      
      // Sort by score descending (best to worst)
      scoresList.sort((a, b) => b.score - a.score);
      
      setBreakdownScoresList(scoresList);
    } catch (err: any) {
      console.error('Failed to load breakdown scores:', err);
    } finally {
      setIsLoadingExecutionList(false);
    }
  };

  const handleResultTypeClick = async (providerId: string, providerName: string, status: string) => {
    if (!testId) return;
    
    setSelectedResultType({ providerId, providerName, status });
    setExecutionListForResultType([]);
    setIsLoadingExecutionList(true);
    
    try {
      // Load all executions for this test
      const executions = await evaluationApi.listExecutions(testId);
      const historyService = ExecutionHistoryService.getInstance();
      
      // Filter executions that have this provider and result type
      const filteredExecutions: Array<{ executionId: string; prompt: string; executedAt: string }> = [];
      
      for (const exec of executions) {
        const providerResult = exec.providerResults.find(pr => pr.providerId === providerId);
        if (providerResult) {
          // Fetch the execution flow to check the status
          try {
            const executionDetail = await historyService.getExecutionById(providerResult.executionId);
            if (executionDetail.flowData?.finalResponse?.status === status) {
              filteredExecutions.push({
                executionId: providerResult.executionId,
                prompt: exec.prompt,
                executedAt: exec.executedAt
              });
            }
          } catch (err) {
            console.warn(`Failed to fetch execution ${providerResult.executionId}:`, err);
          }
        }
      }
      
      setExecutionListForResultType(filteredExecutions);
    } catch (err: any) {
      console.error('Failed to load executions:', err);
    } finally {
      setIsLoadingExecutionList(false);
    }
  };

  const handleCloseExecutionList = () => {
    setSelectedResultType(null);
    setSelectedScore(null);
    setSelectedBreakdown(null);
    setExecutionListForResultType([]);
    setExecutionListForScore([]);
    setBreakdownScoresList([]);
  };

  const handleViewExecution = (executionId: string) => {
    setSelectedExecutionId(executionId);
  };

  const handleCloseExecutionDetail = () => {
    setSelectedExecutionId(null);
  };

  const getScoreColor = (score: number): string => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 8) return 'bg-green-50';
    if (score >= 6) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  const renderScoreDistributionChart = (metrics: ProviderPerformanceMetrics) => {
    const maxCount = Math.max(...Object.values(metrics.scoreDistribution), 1);
    const scores = Array.from({ length: 10 }, (_, i) => i + 1);

    return (
      <div className="space-y-1">
        {scores.map((score) => {
          const count = metrics.scoreDistribution[score] || 0;
          const percentage = (count / maxCount) * 100;
          
          return (
            <button
              key={score}
              onClick={() => count > 0 && handleScoreClick(metrics.providerId, metrics.providerName, score)}
              disabled={count === 0}
              className={`flex items-center gap-2 w-full ${count > 0 ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'} rounded transition-colors`}
            >
              <span className="text-xs font-medium text-gray-600 w-6">{score}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                <div
                  className={`${count > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-200'} h-full transition-all duration-300 flex items-center justify-end pr-2`}
                  style={{ width: `${percentage}%` }}
                >
                  {count > 0 && (
                    <span className="text-xs font-medium text-white">{count}</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const getResultTypeIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'blocked':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'needs_clarification':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'no_data':
      case 'no_relevant_data':
      case 'no_relevant_results':
        return <Inbox className="w-4 h-4 text-gray-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getResultTypeLabel = (status: string): string => {
    switch (status) {
      case 'success':
        return 'Success';
      case 'blocked':
        return 'Blocked';
      case 'needs_clarification':
        return 'Needs Clarification';
      case 'no_data':
        return 'No Matching Opinions';
      case 'no_relevant_data':
        return 'No Relevant Data';
      case 'no_relevant_results':
        return 'No Relevant Results';
      default:
        return status;
    }
  };

  const getResultTypeBgColor = (status: string): string => {
    switch (status) {
      case 'success':
        return 'bg-green-50';
      case 'blocked':
        return 'bg-red-50';
      case 'needs_clarification':
        return 'bg-yellow-50';
      default:
        return 'bg-gray-50';
    }
  };

  const renderAIScoreBreakdownChart = (providerId: string, providerName: string) => {
    const stats = aiScoreStats[providerId];
    if (!stats || stats.count === 0) {
      return null;
    }

    const breakdowns = [
      { 
        label: 'Accuracy', 
        value: stats.breakdownAverages.accuracy, 
        weight: '40%',
        color: 'bg-blue-500',
        lightColor: 'bg-blue-100',
        metric: 'accuracy'
      },
      { 
        label: 'Citation Quality', 
        value: stats.breakdownAverages.citationQuality, 
        weight: '25%',
        color: 'bg-green-500',
        lightColor: 'bg-green-100',
        metric: 'citationQuality'
      },
      { 
        label: 'Completeness', 
        value: stats.breakdownAverages.completeness, 
        weight: '20%',
        color: 'bg-yellow-500',
        lightColor: 'bg-yellow-100',
        metric: 'completeness'
      },
      { 
        label: 'Legal Quality', 
        value: stats.breakdownAverages.legalQuality, 
        weight: '15%',
        color: 'bg-purple-500',
        lightColor: 'bg-purple-100',
        metric: 'legalQuality'
      }
    ];

    return (
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h5 className="text-sm font-semibold text-gray-700 mb-3">AI Score Breakdown (Average)</h5>
        <div className="space-y-3">
          {breakdowns.map((breakdown) => (
            <button
              key={breakdown.label}
              onClick={() => handleBreakdownClick(providerId, providerName, breakdown.metric)}
              className="w-full text-left hover:bg-gray-50 rounded transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between text-sm mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-700 font-medium">{breakdown.label}</span>
                  <span className="text-xs text-gray-500">({breakdown.weight})</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {(breakdown.value * 100).toFixed(1)}%
                </span>
              </div>
              <div className={`w-full ${breakdown.lightColor} rounded-full h-6 overflow-hidden`}>
                <div
                  className={`${breakdown.color} h-6 transition-all duration-300 flex items-center justify-end pr-2`}
                  style={{ width: `${breakdown.value * 100}%` }}
                >
                  {breakdown.value >= 0.15 && (
                    <span className="text-xs font-medium text-white">
                      {(breakdown.value * 10).toFixed(2)}/10
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-3 text-xs text-gray-500">
          Based on {stats.count} AI-scored execution{stats.count !== 1 ? 's' : ''}. Click any metric to see distribution.
        </div>
      </div>
    );
  };

  const renderResultTypeDistribution = (metrics: ProviderPerformanceMetrics) => {
    const totalExecutions = Object.values(metrics.resultTypeDistribution).reduce((sum, count) => sum + count, 0);
    
    if (totalExecutions === 0) {
      return (
        <div className="text-sm text-gray-500 text-center py-4">
          No execution data available
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {Object.entries(metrics.resultTypeDistribution)
          .sort(([, a], [, b]) => b - a)
          .map(([status, count]) => {
            const percentage = ((count / totalExecutions) * 100).toFixed(1);
            
            return (
              <button
                key={status}
                onClick={() => handleResultTypeClick(metrics.providerId, metrics.providerName, status)}
                className={`${getResultTypeBgColor(status)} rounded-lg p-3 flex items-center justify-between w-full hover:ring-2 hover:ring-blue-400 transition-all cursor-pointer`}
              >
                <div className="flex items-center gap-2">
                  {getResultTypeIcon(status)}
                  <span className="text-sm font-medium text-gray-900">
                    {getResultTypeLabel(status)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">{percentage}%</span>
                  <span className="text-sm font-semibold text-gray-900 min-w-[2rem] text-right">
                    {count}
                  </span>
                  <Eye className="w-4 h-4 text-gray-400" />
                </div>
              </button>
            );
          })}
      </div>
    );
  };

  const renderBreakdownDistributionChart = () => {
    if (!selectedBreakdown) return null;

    const getScoreTextColor = (score: number) => {
      if (score >= 0.85) return 'text-green-700';
      if (score >= 0.70) return 'text-blue-700';
      if (score >= 0.50) return 'text-yellow-700';
      return 'text-red-700';
    };

    const getScoreBgColor = (score: number) => {
      if (score >= 0.85) return 'bg-green-50';
      if (score >= 0.70) return 'bg-blue-50';
      if (score >= 0.50) return 'bg-yellow-50';
      return 'bg-red-50';
    };

    return (
      <div className="space-y-1">
        {breakdownScoresList.map((item, idx) => (
          <button
            key={`${item.executionId}-${idx}`}
            onClick={() => handleViewExecution(item.executionId)}
            className={`w-full text-left p-2 border border-gray-200 rounded hover:border-blue-400 hover:shadow-sm transition-all ${getScoreBgColor(item.score)}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className={`px-2 py-0.5 rounded font-bold text-xs ${getScoreTextColor(item.score)}`}>
                  {(item.score * 100).toFixed(1)}%
                </div>
                <span className="text-xs text-gray-500">
                  ({item.overallScore.toFixed(1)}/10)
                </span>
                <p className="text-xs text-gray-900 truncate flex-1">
                  {item.prompt}
                </p>
              </div>
              <Eye className="w-4 h-4 text-blue-600 flex-shrink-0" />
            </div>
          </button>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <AppLayout
        pageTitle="Analytics Overview"
        pageDescription="Performance metrics and insights for evaluation test"
      >
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <button
              onClick={() => navigate('/opinions/evaluation')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tests
            </button>
          </div>
        </div>
        <div data-component="EvaluationAnalyticsView" data-flow="loading" className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200 m-6">
          <div className="text-gray-500">Loading analytics...</div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout
        pageTitle="Analytics Overview"
        pageDescription="Performance metrics and insights for evaluation test"
      >
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <button
              onClick={() => navigate('/opinions/evaluation')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tests
            </button>
          </div>
        </div>
        <div data-component="EvaluationAnalyticsView" data-flow="error" className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200 m-6">
          <div className="text-center text-red-600">
            <XCircle className="w-12 h-12 mx-auto mb-3" />
            <p className="font-medium">Error loading analytics</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!analytics) {
    return (
      <AppLayout
        pageTitle="Analytics Overview"
        pageDescription="Performance metrics and insights for evaluation test"
      >
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <button
              onClick={() => navigate('/opinions/evaluation')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tests
            </button>
          </div>
        </div>
        <div data-component="EvaluationAnalyticsView" data-flow="no-data" className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200 m-6">
          <div className="text-gray-500">No analytics data available</div>
        </div>
      </AppLayout>
    );
  }

  // Check for no executions
  if (analytics.totalExecutions === 0) {
    return (
      <AppLayout
        pageTitle="Analytics Overview"
        pageDescription="Performance metrics and insights for evaluation test"
      >
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <button
              onClick={() => navigate('/opinions/evaluation')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tests
            </button>
          </div>
        </div>
        <div data-component="EvaluationAnalyticsView" data-flow="no-executions" className="bg-white rounded-lg border border-gray-200 p-8 m-6">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Executions Yet</h3>
            <p className="text-gray-600 mb-4">
              Run at least one execution to see analytics
            </p>
            <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg">
              <BarChart3 className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-900">
                Current executions: 0
              </span>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      pageTitle="Analytics Overview"
      pageDescription="Performance metrics and insights for evaluation test"
    >
      {/* Page Actions Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-end gap-3">
          {sourceEvaluationTestId && (
            <button
              data-flow="compare-source"
              onClick={() => navigate(`/opinions/evaluation/${testId}/compare/${sourceEvaluationTestId}`)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <ArrowLeftRight className="w-4 h-4" />
              Compare with Source
            </button>
          )}
          <button
            onClick={() => navigate('/opinions/evaluation')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tests
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Retest Progress Banner */}
        {isAIRetest && (
          <RetestProgressBanner
            progress={retestProgress}
            showCompletedBanner={showCompletedBanner}
            onDismissCompleted={() => setShowCompletedBanner(false)}
            onCancel={async () => {
              if (!testId) return;
              setIsCancellingRetest(true);
              try {
                await evaluationApi.cancelRetest(testId);
                const progress = await evaluationApi.getRetestProgress(testId);
                setRetestProgress(progress);
              } catch (err) {
                console.error('Failed to cancel retest:', err);
              } finally {
                setIsCancellingRetest(false);
              }
            }}
            isCancelling={isCancellingRetest}
          />
        )}

        <div data-component="EvaluationAnalyticsView" data-flow="analytics" className="bg-white rounded-lg border border-gray-200 m-6">
      {/* Header Section */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Analytics Overview</h2>
          <div className="flex items-center gap-3">
            {totalMissingScores > 0 && (
              <button
                onClick={handleEnqueueMissingScores}
                disabled={isEnqueuingScores}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEnqueuingScores ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span className="text-sm font-medium">Enqueuing...</span>
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Generate {totalMissingScores} Missing AI Score{totalMissingScores !== 1 ? 's' : ''}
                    </span>
                  </>
                )}
              </button>
            )}
            <button
              onClick={handleRerunAllScores}
              disabled={isEnqueuingScores}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEnqueuingScores ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span className="text-sm font-medium">Re-running...</span>
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm font-medium">Re-run All AI Scores</span>
                </>
              )}
            </button>
            {analytics.executionLimitExceeded && (
              <div className="flex items-center gap-2 bg-orange-50 px-3 py-1.5 rounded-lg">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-900">
                  Execution limit exceeded
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          {/* Total Executions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Total Executions</span>
            </div>
            <p className="text-3xl font-bold text-blue-900">{analytics.totalExecutions}</p>
          </div>

          {/* Providers Tested */}
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">Providers Tested</span>
            </div>
            <p className="text-3xl font-bold text-purple-900">{analytics.providerMetrics.length}</p>
          </div>

          {/* Unique Users */}
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">Unique Users</span>
            </div>
            <p className="text-3xl font-bold text-green-900">
              {Object.keys(analytics.executionCountsByUser).length}
            </p>
          </div>
        </div>
      </div>

      {/* Provider Metrics Section */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Provider Performance Metrics</h3>
        <p className="text-sm text-gray-600 mb-6">
          Compare average scores and consistency across providers. You decide which performs better based on these metrics.
        </p>

        <div className="space-y-6">
          {analytics.providerMetrics.map((metrics) => (
            <div
              key={metrics.providerId}
              className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              {/* Provider Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{metrics.providerName}</h4>
                  <p className="text-sm text-gray-500">Provider ID: {metrics.providerId}</p>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold ${getScoreColor(metrics.averageScore)}`}>
                    {metrics.averageScore.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-500">Average Score</div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                {/* Average Score Detail */}
                <div className={`${getScoreBgColor(metrics.averageScore)} rounded-lg p-3`}>
                  <div className="text-xs font-medium text-gray-600 mb-1">User Avg Score</div>
                  <div className={`text-2xl font-bold ${getScoreColor(metrics.averageScore)}`}>
                    {metrics.averageScore.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">out of 10</div>
                </div>

                {/* AI Average Score */}
                {aiScoreStats[metrics.providerId] && aiScoreStats[metrics.providerId].count > 0 && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-gray-600 mb-1">AI Avg Score</div>
                    <div className="text-2xl font-bold text-purple-900">
                      {aiScoreStats[metrics.providerId].average.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {aiScoreStats[metrics.providerId].count} scored
                      {aiScoreStats[metrics.providerId].missing > 0 && (
                        <span className="text-orange-600"> • {aiScoreStats[metrics.providerId].missing} missing</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Standard Deviation */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-600 mb-1">Std Deviation</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {metrics.standardDeviation.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {metrics.standardDeviation < 1.5 ? 'Very consistent' : 
                     metrics.standardDeviation < 2.5 ? 'Consistent' : 'Variable'}
                  </div>
                </div>

                {/* Total Scores */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-600 mb-1">Total Scores</div>
                  <div className="text-2xl font-bold text-gray-900">{metrics.totalScores}</div>
                  <div className="text-xs text-gray-500 mt-1">executions scored</div>
                </div>
              </div>

              {/* Score Distribution Chart */}
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Score Distribution</h5>
                {renderScoreDistributionChart(metrics)}
              </div>

              {/* AI Score Breakdown Chart */}
              {renderAIScoreBreakdownChart(metrics.providerId, metrics.providerName)}

              {/* Result Type Distribution */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Result Type Distribution</h5>
                {renderResultTypeDistribution(metrics)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Execution Counts by User Section */}
      <div className="p-6 border-t border-gray-200 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Execution Counts by User</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(analytics.executionCountsByUser)
            .sort(([, a], [, b]) => b - a)
            .map(([userId, count]) => (
              <div
                key={userId}
                className="bg-white rounded-lg p-3 border border-gray-200 flex items-center justify-between"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 truncate" title={userId}>
                    {userId}
                  </span>
                </div>
                <span className="text-sm font-semibold text-blue-600 ml-2">
                  {count}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Footer Note */}
      <div className="p-4 bg-blue-50 border-t border-blue-100">
        <div className="flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Manual Decision Required</p>
            <p className="text-blue-700">
              These metrics are provided for your analysis. Review the average scores, consistency (standard deviation), 
              and score distributions to manually determine which provider configuration performs better for your use case.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>

    {/* Execution List Modal */}
    {(selectedResultType || selectedScore || selectedBreakdown) && (
      <div data-component="ExecutionListModal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              {selectedResultType && (
                <>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedResultType.providerName} - {getResultTypeLabel(selectedResultType.status)} Executions
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {executionListForResultType.length} execution{executionListForResultType.length !== 1 ? 's' : ''} found
                  </p>
                </>
              )}
              {selectedScore && (
                <>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedScore.providerName} - Score {selectedScore.score} Executions
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {executionListForScore.length} score{executionListForScore.length !== 1 ? 's' : ''} found
                  </p>
                </>
              )}
              {selectedBreakdown && (
                <>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedBreakdown.providerName} - {(() => {
                      switch (selectedBreakdown.metric) {
                        case 'accuracy': return 'Accuracy';
                        case 'citationQuality': return 'Citation Quality';
                        case 'completeness': return 'Completeness';
                        case 'legalQuality': return 'Legal Quality';
                        default: return selectedBreakdown.metric;
                      }
                    })()} Distribution
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    AI score breakdown distribution
                  </p>
                </>
              )}
            </div>
            <button
              onClick={handleCloseExecutionList}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XCircle className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Execution List */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoadingExecutionList ? (
              <div className="flex items-center justify-center h-32 text-gray-500">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span>Loading...</span>
                </div>
              </div>
            ) : selectedBreakdown ? (
              <div>
                {breakdownScoresList.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-gray-500">
                    No scores available
                  </div>
                ) : (
                  <div>
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-900">
                        All executions sorted by {(() => {
                          switch (selectedBreakdown.metric) {
                            case 'accuracy': return 'Accuracy';
                            case 'citationQuality': return 'Citation Quality';
                            case 'completeness': return 'Completeness';
                            case 'legalQuality': return 'Legal Quality';
                            default: return selectedBreakdown.metric;
                          }
                        })()} score (best to worst). Click any execution to view details.
                      </p>
                    </div>
                    {renderBreakdownDistributionChart()}
                  </div>
                )}
              </div>
            ) : selectedResultType && executionListForResultType.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-500">
                No executions found
              </div>
            ) : selectedScore && executionListForScore.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-500">
                No scores found
              </div>
            ) : (
              <div className="space-y-2">
                {selectedResultType && executionListForResultType.map((exec) => (
                  <button
                    key={exec.executionId}
                    onClick={() => handleViewExecution(exec.executionId)}
                    className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                          {exec.prompt}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(exec.executedAt).toLocaleString()}
                        </p>
                      </div>
                      <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                ))}
                {selectedScore && executionListForScore.map((exec, idx) => (
                  <button
                    key={`${exec.executionId}-${idx}`}
                    onClick={() => handleViewExecution(exec.executionId)}
                    className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${getScoreBgColor(exec.score)} ${getScoreColor(exec.score)}`}>
                            Score: {exec.score}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                          {exec.prompt}
                        </p>
                        {exec.note && (
                          <p className="text-xs text-gray-600 italic mb-1 line-clamp-1">
                            "{exec.note}"
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {new Date(exec.executedAt).toLocaleString()}
                        </p>
                      </div>
                      <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Execution Detail Modal */}
    {selectedExecutionId && (
      <ExecutionHistoryDetail
        executionId={selectedExecutionId}
        onClose={handleCloseExecutionDetail}
      />
    )}
    </AppLayout>
  );
};
